import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowIcon } from './ClayShapes';
import ClayIcon from './ClayIcon';

/* ---------- Capability marquee (replaces placeholder logo strip) ---------- */

const MARQUEE = [
  'Same-day medicine delivery',
  'Live GPS tracking',
  'Proof of delivery',
  'Cash & cheque collections',
  'Attendance, leave & TADA',
  'ZeroMDS accounting sync',
  'Rider · Salesman · Employee roles',
  'Fleet SaaS anywhere in India',
];

export function CapabilityMarquee() {
  const items = [...MARQUEE, ...MARQUEE];
  return (
    <div className="marquee glass gs-up" role="region" aria-label="What FastWhistle covers">
      <span className="fw-mono marquee-label">BUILT FOR PHARMA DISTRIBUTORS</span>
      <div className="marquee-track-wrap">
        <ul className="marquee-track">
          {items.map((text, i) => (
            <li key={`${text}-${i}`} aria-hidden={i >= MARQUEE.length ? 'true' : undefined}>
              <span className="marquee-dot" />
              {text}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ---------- Interactive product tour with real screenshots ---------- */

export const TOUR = [
  {
    key: 'live',
    label: 'Live tracking',
    title: 'See every salesman and rider on the map.',
    text: 'Filter by person, date and fleet type. Know who is where before the phone call.',
    img: '/images/fleet/live-locations-map.webp',
    w: 1024,
    h: 768,
  },
  {
    key: 'routes',
    label: 'Route timeline',
    title: 'Replay any day, stop by stop.',
    text: 'Timeline plus map with travel time and collections per stop — settle disputes in seconds.',
    img: '/images/fleet/activity-timeline-map.webp',
    w: 1024,
    h: 747,
  },
  {
    key: 'activity',
    label: 'Field activity',
    title: 'Visits, orders and collections in one feed.',
    text: 'Every visit, order and reach proof logged against the right party, automatically.',
    img: '/images/fleet/user-activities.webp',
    w: 741,
    h: 1024,
  },
  {
    key: 'dash',
    label: 'Fleet dashboard',
    title: 'Your whole field team at a glance.',
    text: 'Live users, today’s orders and activity KPIs on the screen your manager opens first.',
    img: '/images/fleet/fleet-dashboard.webp',
    w: 907,
    h: 1024,
  },
  {
    key: 'app',
    label: 'Mobile app',
    title: 'One app for the people on the road.',
    text: 'Riders, salesmen and employees switch roles in one app. Attendance, tasks and orders with OTP login.',
    phones: ['/images/mobile/role-selector.webp', '/images/mobile/salesman-dashboard.webp', '/images/mobile/attendance.webp'],
  },
];

export function ProductTour({
  items = TOUR,
  id = 'product',
  eyebrow = 'Product tour',
  title = (
    <>
      Real screens. <span className="hl">No mock-ups.</span>
    </>
  ),
  subtitle = 'This is the software distributors use every day — on the web for managers, on Android for the field.',
  ctaTo = '/fleet-saas',
  ctaLabel = 'Explore Fleet SaaS →',
}) {
  const [tab, setTab] = useState(0);
  const frameRef = useRef(null);
  const item = items[tab];

  const onMove = (e) => {
    const el = frameRef.current;
    if (!el || !window.matchMedia('(pointer: fine)').matches) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    el.style.setProperty('--rx', `${(-y * 8).toFixed(2)}deg`);
    el.style.setProperty('--ry', `${(x * 10).toFixed(2)}deg`);
  };
  const onLeave = () => {
    const el = frameRef.current;
    if (!el) return;
    el.style.setProperty('--rx', '0deg');
    el.style.setProperty('--ry', '0deg');
  };

  const onKey = (e) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const next = (tab + (e.key === 'ArrowRight' ? 1 : -1) + items.length) % items.length;
    setTab(next);
    document.getElementById(`tour-tab-${items[next].key}`)?.focus();
  };

  return (
    <section id={id} className="fw-wrap" style={{ paddingTop: 96, paddingBottom: 40 }}>
      <div className="section-head">
        <div>
          <div className="fw-eyebrow gs-up">{eyebrow}</div>
          <h2 className="fw-h2 gs-up" style={{ maxWidth: 720 }}>{title}</h2>
        </div>
        <p className="fw-sub gs-up" style={{ flex: '0 1 400px', margin: 0 }}>{subtitle}</p>
      </div>

      <div className="tour-tabs gs-up" role="tablist" aria-label="Product screens" onKeyDown={onKey}>
        {items.map((t, i) => (
          <button
            key={t.key}
            id={`tour-tab-${t.key}`}
            type="button"
            role="tab"
            aria-selected={i === tab}
            aria-controls="tour-panel"
            tabIndex={i === tab ? 0 : -1}
            className={`tour-tab${i === tab ? ' is-active' : ''}`}
            onClick={(e) => {
                  setTab(i);
                  // On phones the tab bar scrolls sideways; bring the tapped tab fully into view.
                  e.currentTarget.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
                }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div id="tour-panel" role="tabpanel" aria-labelledby={`tour-tab-${item.key}`} className="tour-panel glass gs-up">
        <div className="tour-copy">
          <h3 className="fw-display" key={`h-${item.key}`}>{item.title}</h3>
          <p key={`p-${item.key}`}>{item.text}</p>
          {ctaTo && <Link to={ctaTo} className="lnk">{ctaLabel}</Link>}
        </div>
        <div className="tour-stage" onMouseMove={onMove} onMouseLeave={onLeave}>
          <div className="tour-frame" ref={frameRef} key={item.key}>
            {item.phones ? (
              <div className="tour-phones">
                {item.phones.map((src, i) => (
                  <div key={src} className="phone-frame" style={{ '--n': i }}>
                    <img src={src} alt="FastWhistle fleet app screen" loading="lazy" width="456" height="1024" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="browser-frame">
                <div className="browser-bar" aria-hidden="true">
                  <span /> <span /> <span />
                  <em className="fw-mono">panel.fastwhistle.com</em>
                </div>
                <div className="browser-body">
                  <img src={item.img} alt={item.title} loading="lazy" width={item.w} height={item.h} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Ecosystem: five people, one loop ---------- */

const ROLES = [
  { n: '1', who: 'Salesman / MR', what: 'Visits the chemist, books the order in the app.' },
  { n: '2', who: 'Admin / Manager', what: 'Sees live orders, the team on the map and reports.' },
  { n: '3', who: 'Warehouse', what: 'Picks, packs and assigns the batch to a rider.' },
  { n: '4', who: 'Rider', what: 'Navigates, delivers and updates status at the door.' },
  { n: '5', who: 'Chemist', what: 'Gets stock on time with digital proof of delivery.' },
];

export function Ecosystem() {
  return (
    <section id="ecosystem" className="fw-wrap" style={{ paddingTop: 72, paddingBottom: 40 }}>
      <div className="glass gs-up eco">
        <div className="eco-copy">
          <div className="fw-eyebrow">The whole loop</div>
          <h2 className="fw-h2" style={{ fontSize: 'clamp(30px, 3.8vw, 48px)' }}>
            Five people. One order. <span className="hl">Zero phone calls.</span>
          </h2>
          <ol className="eco-roles">
            {ROLES.map((r) => (
              <li key={r.n} className="gs-li">
                <span className="eco-n fw-display">{r.n}</span>
                <div>
                  <strong>{r.who}</strong>
                  <span>{r.what}</span>
                </div>
              </li>
            ))}
          </ol>
        </div>
        <figure className="eco-art icon-idle-slow">
          <img
            src="/images/fleet/fleet-ecosystem.webp"
            alt="FastWhistle ecosystem: salesman places order, manager monitors, warehouse packs, rider delivers, pharmacy receives"
            loading="lazy"
            width="1200"
            height="800"
          />
        </figure>
      </div>
    </section>
  );
}

/* ---------- Before / after toggle ---------- */

const COMPARE = [
  { without: 'Calling riders to ask “where are you?”', with: 'Every rider live on one map' },
  { without: 'Paper registers for attendance', with: 'Check-in from the app, leave and TADA built in' },
  { without: 'Cash collections noted in a diary', with: 'Collections recorded at the counter, reconciled daily' },
  { without: 'Re-typing orders from accounting', with: 'Parties, products and orders synced from ZeroMDS' },
  { without: 'Chemist says “stock never came”', with: 'Proof of delivery with time and location' },
];

export function BeforeAfter() {
  const [on, setOn] = useState(true);
  const [touched, setTouched] = useState(false); // ticks pop only after the visitor toggles
  return (
    <section className="fw-wrap" style={{ paddingTop: 72, paddingBottom: 40 }}>
      <div className="section-head">
        <div>
          <div className="fw-eyebrow gs-up">Why distributors switch</div>
          <h2 className="fw-h2 gs-up" style={{ maxWidth: 680 }}>
            Your day, <span className="hl">{on ? 'with' : 'without'}</span> FastWhistle.
          </h2>
        </div>
        <div className="ba-toggle glass gs-up" role="group" aria-label="Compare">
          <button type="button" aria-pressed={!on} className={!on ? 'is-on' : ''} onClick={() => { setOn(false); setTouched(true); }}>Without</button>
          <button type="button" aria-pressed={on} className={on ? 'is-on is-with' : ''} onClick={() => { setOn(true); setTouched(true); }}>With FastWhistle</button>
        </div>
      </div>
      <ul className={`ba-list${on ? ' is-with' : ''}${touched ? ' is-touched' : ''}`}>
        {COMPARE.map((row) => (
          <li key={row.with} className="glass gs-card">
            {on ? (
              <ClayIcon name="check" size={40} fallback={<span className="ba-icon" aria-hidden="true">✓</span>} />
            ) : (
              <span className="ba-icon" aria-hidden="true">✕</span>
            )}
            <span className="ba-text">{on ? row.with : row.without}</span>
          </li>
        ))}
      </ul>
      <div className="gs-up" style={{ marginTop: 28 }}>
        <Link to="/contact-us" className="clay btn btn-mag pill-primary">
          See it on your own routes <ArrowIcon />
        </Link>
      </div>
    </section>
  );
}
